import authService from "../services/authService";

export const ROLES = {
  ADMIN: "admin",
  TENANT_ADMIN: "tenant_admin",
  VIEWER: "viewer",
  USER: "user",
  REPORTER: "reporter",
} as const;

export type AppRole = (typeof ROLES)[keyof typeof ROLES];

const WORKSPACE_ROLES = new Set<string>([
  ROLES.TENANT_ADMIN,
  ROLES.USER,
  ROLES.REPORTER,
  ROLES.VIEWER,
]);

const GROUP_NAME_TO_ROLE: Record<string, string> = {
  admin: ROLES.TENANT_ADMIN,
  user: ROLES.USER,
  reporter: ROLES.REPORTER,
  viewer: ROLES.VIEWER,
};

export const normalizeRoles = (roles: any): string[] => {
  if (!Array.isArray(roles)) {
    return [];
  }
  return roles
    .filter(Boolean)
    .map((role: any) => String(role).toLowerCase().trim());
};

/**
 * Reads JWT payload JSON (browser-safe). Used because Keycloak often puts `realm_access` /
 * `resource_access` only on the access token, while oidc-client `profile` comes from ID token + userinfo.
 */
export const decodeJwtPayload = (jwt?: string | null): Record<string, any> | null => {
  if (!jwt || typeof jwt !== "string") return null;
  try {
    const parts = jwt.split(".");
    if (parts.length < 2) return null;
    let b64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const pad = (4 - (b64.length % 4)) % 4;
    if (pad) b64 += "=".repeat(pad);
    const json = atob(b64);
    const payload = JSON.parse(json);
    return payload && typeof payload === "object" ? payload : null;
  } catch {
    return null;
  }
};

const collectGroups = (...sources: unknown[]): string[] => {
  const out = new Set<string>();
  for (const source of sources) {
    if (!Array.isArray(source)) continue;
    for (const item of source) {
      if (item) out.add(String(item).trim());
    }
  }
  return [...out];
};

/**
 * `/admin` → platform super admin.
 * `/{tenant}/{admin|user|reporter|viewer}` → workspace role (`admin` becomes `tenant_admin`).
 */
export const rolesFromGroups = (groups: unknown): { superAdmin: boolean; workspaceRoles: string[] } => {
  const workspaceRoles = new Set<string>();
  let superAdmin = false;
  if (!Array.isArray(groups)) {
    return { superAdmin, workspaceRoles: [] };
  }
  for (const raw of groups) {
    if (!raw) continue;
    const segments = String(raw).trim().replace(/^\/+/, "").split("/").filter(Boolean);
    if (segments.length === 1 && segments[0] === "admin") {
      superAdmin = true;
      continue;
    }
    if (segments.length === 2) {
      const mapped = GROUP_NAME_TO_ROLE[segments[1]];
      if (mapped) workspaceRoles.add(mapped);
    }
  }
  return { superAdmin, workspaceRoles: [...workspaceRoles] };
};

export const extractRolesFromUser = (user: any): string[] => {
  if (!user) {
    return [];
  }

  const profile = user.profile || {};
  const accessPayload = decodeJwtPayload(user.access_token);
  const idPayload = decodeJwtPayload(user.id_token);

  const profileRoles = normalizeRoles(profile.roles);
  const realmRoles = normalizeRoles(
    profile.realm_access?.roles ??
      accessPayload?.realm_access?.roles ??
      idPayload?.realm_access?.roles
  );
  const resourceFromProfile = Object.values(profile.resource_access || {}).flatMap(
    (resource: any) => resource?.roles || []
  );
  const resourceFromAccess = Object.values(accessPayload?.resource_access || {}).flatMap(
    (resource: any) => resource?.roles || []
  );
  const resourceFromId = Object.values(idPayload?.resource_access || {}).flatMap(
    (resource: any) => resource?.roles || []
  );
  const resourceRoles = normalizeRoles([
    ...resourceFromProfile,
    ...resourceFromAccess,
    ...resourceFromId,
  ]);

  const groups = collectGroups(
    profile.groups,
    accessPayload?.groups,
    idPayload?.groups,
    profile.user?.groups,
    accessPayload?.user?.groups,
    idPayload?.user?.groups
  );
  const { superAdmin, workspaceRoles } = rolesFromGroups(groups);

  const merged = new Set([...profileRoles, ...realmRoles, ...resourceRoles]);
  if (workspaceRoles.length > 0) {
    for (const role of [...merged]) {
      if (WORKSPACE_ROLES.has(role)) merged.delete(role);
    }
    workspaceRoles.forEach((role) => merged.add(role));
  }
  if (superAdmin) {
    merged.add(ROLES.ADMIN);
  }

  return [...merged];
};

export const getCurrentUserRoles = async (): Promise<string[]> => {
  try {
    const user = await authService.getUser();
    return extractRolesFromUser(user);
  } catch (error) {
    console.error("Failed to load user roles:", error);
    return [];
  }
};

export const hasRole = (roles: string[], role: string) => roles.includes(role.toLowerCase());

export const isAdmin = (roles: string[]) => hasRole(roles, ROLES.ADMIN);

export const isTenantAdmin = (roles: string[]) => hasRole(roles, ROLES.TENANT_ADMIN);

/** Platform super admin or workspace tenant admin. */
export const isWorkspaceAdmin = (roles: string[]) => isAdmin(roles) || isTenantAdmin(roles);

/** Landing route after password/OIDC login. */
export const postLoginPath = (_roles: string[]) => "/home";

/**
 * Signed-in user with no platform admin and no workspace group roles must finish
 * Google / SaaS workspace onboarding before using the app chrome.
 */
export const needsWorkspaceOnboarding = (roles: string[]) => {
  if (!roles || roles.length === 0) return true;
  if (isAdmin(roles)) return false;
  return !roles.some((role) => WORKSPACE_ROLES.has(role));
};

/** Sidenav items shown to platform super admins. */
export const SUPER_ADMIN_MENU_KEYS = new Set([
  "home",
  "sa-dashboard",
  "sa-plans",
  "sa-customers",
  "sa-datasources",
  "sa-ui-docs",
  "sa-mail",
  "sa-workspaces",
  "sa-users",
  "sa-invites",
  "sa-embeds",
  "sa-integrations",
  "sa-audit",
  "sa-errors",
  "docs",
  "release-notes",
]);

/** Routes platform super admins may open (menu + nested docs). */
export const SUPER_ADMIN_ROUTE_KEYS = new Set([
  ...SUPER_ADMIN_MENU_KEYS,
  "super-admin",
  "super-admin-section",
  "docs-section",
]);

export const isViewer = (roles: string[]) =>
  hasRole(roles, ROLES.VIEWER) && !isWorkspaceAdmin(roles);

/** Dashboard PDF/Excel export — viewer role excluded. */
export const canExportDashboard = (roles: string[]) =>
  isWorkspaceAdmin(roles) || hasRole(roles, ROLES.USER) || hasRole(roles, ROLES.REPORTER);

/**
 * Route / menu access.
 * Platform super admins only get the portal + help pages when {@code routeKey} is provided.
 * Capability checks that omit {@code routeKey} still treat admin as allowed.
 */
export const canAccessRoute = (roles: string[], allowedRoles?: string[], routeKey?: string) => {
  if (isAdmin(roles)) {
    if (routeKey) {
      return SUPER_ADMIN_ROUTE_KEYS.has(routeKey);
    }
    return true;
  }
  if (!allowedRoles || allowedRoles.length === 0) {
    return true;
  }
  return roles.some((role) => allowedRoles.includes(role));
};
